import { AxiosError } from 'axios';
import * as React from 'react';
import styled from 'styled-components';

import useFocus from 'src/CustomHooks/useFocus';
import useProgressBar from 'src/CustomHooks/useProgressBar';
import ISubmission from 'src/ServerEntities/Submission';
import Button from 'src/SharedComponents/Button';
import ButtonContainer from 'src/SharedComponents/ButtonContainer';
import ConfirmationToast from 'src/SharedComponents/ConfirmationToast';
import ContentWrapper from 'src/SharedComponents/ContentWrapper';
import ErrorToast from 'src/SharedComponents/ErrorToast';
import LoadingIndicator from 'src/SharedComponents/LoadingIndicator';
import ProgressBar from 'src/SharedComponents/ProgressBar';
import QuestionList from 'src/SharedComponents/QuestionList';

import UnlabeledMultipleChoiceQuestion from 'src/SharedComponents/UnlabeledMultipleChoiceQuestion';

import { getAnswers, saveAnswer, submitForm } from 'src/service';
import { SessionContext } from 'src/Views/SessionContext';

import DialogActions from 'src/SharedComponents/Dialog/DialogActions';
import DialogContentText from 'src/SharedComponents/Dialog/DialogContentText';

interface ILabelContainerProps {
    isScrolled: boolean
};

const FixedContainer = styled.div`
    position: fixed;
    top: ${(props: ILabelContainerProps) => props.isScrolled ? "0" : "10px"};
    left: 10px;
    background: #005eb8;
    width: calc(100% - 20px);
`;

const LabelContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    padding: 12px;
    line-height: 1;
`;

const FixedPadding = styled.div`
    height: 144px;
`;

const Label = styled.p`
    height: 120px;
    width: 24px;
    margin: 0 6px;
    writing-mode: vertical-rl;
    transform: rotate(-180deg);
    font-size: 1.1rem;
    font-weight: 600;
`;

const answers = ["Never", "Sometimes", "Often", "Always"];
const QUESTION_IDS = [35, 36, 37, 38, 39, 40, 41, 42, 43, 44, 45, 46, 47, 48, 49, 50, 51, 52, 53, 54, 55, 56, 57, 58, 59, 60, 61, 62, 63, 64, 65, 66, 67, 68, 69, 70, 71, 72, 73, 74, 75, 76, 77, 78, 79, 80, 81];

interface IProps {
    token: string
};

const RcadsPage1 = (props: IProps) => {
    const sessionContext = React.useContext(SessionContext);
    const { webToken } = sessionContext.state;
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState("");
    const [isScrolled, setIsScrolled] = React.useState(false);
    const { dispatch, state } = React.useContext(SessionContext);
    const { answered, numberAnswered, updateAnswered, updateAllAnswered, getAnswered, includedInArray, ERROR_SAVING_MESSAGE } = useProgressBar();
    const questionRefs = QUESTION_IDS.map(questionId => React.useRef(null as unknown as HTMLLIElement));
    const [saveDialog, setSaveDialog] = React.useState(false);
    const {htmlElRef, setFocus} = useFocus<HTMLButtonElement>();
    
    React.useEffect(() => {
        if (webToken) {
            getAnswers(props.token, webToken, (submissionResponse: ISubmission) => {
                const questionAnswers = submissionResponse.answers.map(a => {
                    return {
                        isAnswered: true,
                        itemIndex: answers.indexOf(a.answer),
                        questionId: a.questionId
                    };
                });
                updateAllAnswered(questionAnswers);
            }, (aError: AxiosError) => {
                setError("There was an error loading previous answers. Please fill in all questions.");
            });
        }
    }, [webToken]);

    const checkUnauthorised = (serverError: AxiosError) => {
        if (serverError.response && serverError.response.data.code === 401) {
            dispatch({ type: "clearWebToken", payload: undefined });
        }
    }

    const handleError = (serverError: AxiosError, questionId: number) => {
        checkUnauthorised(serverError);
        setError(ERROR_SAVING_MESSAGE)
        updateAnswered(questionId, false, -1);
    };

    const handleSubmissionError = (serverError: AxiosError) => {
        checkUnauthorised(serverError);
        if (serverError.response && serverError.response.status === 409) {
            setSaveDialog(true);
            setFocus();
            setLoading(false);
        }
        else {
            setError(ERROR_SAVING_MESSAGE)
        }
    };

    const onAnswerChange = (questionId: number, answer: string, itemIndex?: number) => {
        setError("");
        saveAnswer(questionId, answer, state.webToken, () => {
            updateAnswered(questionId, !!answer, itemIndex !== undefined ? itemIndex : -1);
        }, handleError)
    };

    const goToNextPage = () => {
        setLoading(true);
        submitForm(state.webToken, () => {
            dispatch({ type: "setPage", payload: { formId: 2, page: 2 } });
        }, handleSubmissionError, "check");
    };

    const onDisabledClick = () => {
        const firstUnansweredIndex = QUESTION_IDS.findIndex(requiredAnswerId => !includedInArray(answered, requiredAnswerId));
        if (firstUnansweredIndex > -1) {
            questionRefs[firstUnansweredIndex].current.scrollIntoView({ behavior: 'smooth', block: 'center' })
            setError("Please answer all questions");
        }
    };

    React.useEffect(() => {
        const updateScrolled = () => {
            setIsScrolled(window.pageYOffset > 0);
        };

        window.addEventListener('scroll', updateScrolled);
        return () => {
            window.removeEventListener('scroll', updateScrolled);
        };
    }, []);

    const closeDialog = () => {
        setSaveDialog(false);
    };

    const saveForm = () => {
        setLoading(true);
        setSaveDialog(false);
        submitForm(state.webToken, () => {
            dispatch({ type: "setPage", payload: { formId: 2, page: 2 } });
        }, handleSubmissionError);
    };

    return <ContentWrapper>
        <FixedPadding />
        <FixedContainer isScrolled={isScrolled}>
            <LabelContainer>
                <Label>Never</Label>
                <Label>Sometimes</Label>
                <Label>Often</Label>
                <Label>Always</Label>
            </LabelContainer>
        </FixedContainer>
        <QuestionList>
            <UnlabeledMultipleChoiceQuestion checkedItem={getAnswered(35)} refProp={questionRefs[0]} question="I worry about things" answers={answers} questionId={35} onAnswerChange={onAnswerChange} />
            <UnlabeledMultipleChoiceQuestion checkedItem={getAnswered(36)} refProp={questionRefs[1]} question="I feel sad or empty" answers={answers} questionId={36} onAnswerChange={onAnswerChange} />
            <UnlabeledMultipleChoiceQuestion checkedItem={getAnswered(37)} refProp={questionRefs[2]} question="When I have a problem, I get a funny feeling in my stomach" answers={answers} questionId={37} onAnswerChange={onAnswerChange} />
            <UnlabeledMultipleChoiceQuestion checkedItem={getAnswered(38)} refProp={questionRefs[3]} question="I worry when I think I have done poorly at something" answers={answers} questionId={38} onAnswerChange={onAnswerChange} />
            <UnlabeledMultipleChoiceQuestion checkedItem={getAnswered(39)} refProp={questionRefs[4]} question="I would feel afraid of being on my own at home" answers={answers} questionId={39} onAnswerChange={onAnswerChange} />
            <UnlabeledMultipleChoiceQuestion checkedItem={getAnswered(40)} refProp={questionRefs[5]} question="Nothing is much fun anymore" answers={answers} questionId={40} onAnswerChange={onAnswerChange} />
            <UnlabeledMultipleChoiceQuestion checkedItem={getAnswered(41)} refProp={questionRefs[6]} question="I feel scared when I have to take a test" answers={answers} questionId={41} onAnswerChange={onAnswerChange} />
            <UnlabeledMultipleChoiceQuestion checkedItem={getAnswered(42)} refProp={questionRefs[7]} question="I feel worried when I think someone is angry with me" answers={answers} questionId={42} onAnswerChange={onAnswerChange} />
            <UnlabeledMultipleChoiceQuestion checkedItem={getAnswered(43)} refProp={questionRefs[8]} question="I worry about being away from my parents" answers={answers} questionId={43} onAnswerChange={onAnswerChange} />
            <UnlabeledMultipleChoiceQuestion checkedItem={getAnswered(44)} refProp={questionRefs[9]} question="I get bothered by bad or silly thoughts or pictures in my  mind" answers={answers} questionId={44} onAnswerChange={onAnswerChange} />
            <UnlabeledMultipleChoiceQuestion checkedItem={getAnswered(45)} refProp={questionRefs[10]} question="I have trouble sleeping" answers={answers} questionId={45} onAnswerChange={onAnswerChange} />
            <UnlabeledMultipleChoiceQuestion checkedItem={getAnswered(46)} refProp={questionRefs[11]} question="I worry that I will do badly at my school work" answers={answers} questionId={46} onAnswerChange={onAnswerChange} />
            <UnlabeledMultipleChoiceQuestion checkedItem={getAnswered(47)} refProp={questionRefs[12]} question="I worry that something awful will happen to someone in my family" answers={answers} questionId={47} onAnswerChange={onAnswerChange} />
            <UnlabeledMultipleChoiceQuestion checkedItem={getAnswered(48)} refProp={questionRefs[13]} question="I suddenly feel as if I can't breathe when there is no reason for this" answers={answers} questionId={48} onAnswerChange={onAnswerChange} />
            <UnlabeledMultipleChoiceQuestion checkedItem={getAnswered(49)} refProp={questionRefs[14]} question="I have problems with my appetite" answers={answers} questionId={49} onAnswerChange={onAnswerChange} />
            <UnlabeledMultipleChoiceQuestion checkedItem={getAnswered(50)} refProp={questionRefs[15]} question="I have to keep checking that I have done things right (like the switch is off, or the door is locked)" answers={answers} questionId={50} onAnswerChange={onAnswerChange} />
            <UnlabeledMultipleChoiceQuestion checkedItem={getAnswered(51)} refProp={questionRefs[16]} question="I feel scared if I have to sleep on my own" answers={answers} questionId={51} onAnswerChange={onAnswerChange} />
            <UnlabeledMultipleChoiceQuestion checkedItem={getAnswered(52)} refProp={questionRefs[17]} question="I have trouble going to school in the mornings because I feel nervous or afraid" answers={answers} questionId={52} onAnswerChange={onAnswerChange} />
            <UnlabeledMultipleChoiceQuestion checkedItem={getAnswered(53)} refProp={questionRefs[18]} question="I have no energy for things" answers={answers} questionId={53} onAnswerChange={onAnswerChange} />
            <UnlabeledMultipleChoiceQuestion checkedItem={getAnswered(54)} refProp={questionRefs[19]} question="I worry I might look foolish" answers={answers} questionId={54} onAnswerChange={onAnswerChange} />
            <UnlabeledMultipleChoiceQuestion checkedItem={getAnswered(55)} refProp={questionRefs[20]} question="I am tired a lot" answers={answers} questionId={55} onAnswerChange={onAnswerChange} />
            <UnlabeledMultipleChoiceQuestion checkedItem={getAnswered(56)} refProp={questionRefs[21]} question="I worry that bad things will happen to me" answers={answers} questionId={56} onAnswerChange={onAnswerChange} />
            <UnlabeledMultipleChoiceQuestion checkedItem={getAnswered(57)} refProp={questionRefs[22]} question="I can't seem to get bad or silly thoughts out of my head" answers={answers} questionId={57} onAnswerChange={onAnswerChange} />
            <UnlabeledMultipleChoiceQuestion checkedItem={getAnswered(58)} refProp={questionRefs[23]} question="When I have a problem, my heart beats really fast" answers={answers} questionId={58} onAnswerChange={onAnswerChange} />
            <UnlabeledMultipleChoiceQuestion checkedItem={getAnswered(59)} refProp={questionRefs[24]} question="I cannot think clearly" answers={answers} questionId={59} onAnswerChange={onAnswerChange} />
            <UnlabeledMultipleChoiceQuestion checkedItem={getAnswered(60)} refProp={questionRefs[25]} question="I suddenly start to tremble or shake when there is no reason for this" answers={answers} questionId={60} onAnswerChange={onAnswerChange} />
            <UnlabeledMultipleChoiceQuestion checkedItem={getAnswered(61)} refProp={questionRefs[26]} question="I worry that something bad will happen to me" answers={answers} questionId={61} onAnswerChange={onAnswerChange} />
            <UnlabeledMultipleChoiceQuestion checkedItem={getAnswered(62)} refProp={questionRefs[27]} question="When I have a problem, I feel shaky" answers={answers} questionId={62} onAnswerChange={onAnswerChange} />
            <UnlabeledMultipleChoiceQuestion checkedItem={getAnswered(63)} refProp={questionRefs[28]} question="I feel worthless" answers={answers} questionId={63} onAnswerChange={onAnswerChange} />
            <UnlabeledMultipleChoiceQuestion checkedItem={getAnswered(64)} refProp={questionRefs[29]} question="I worry about making mistakes" answers={answers} questionId={64} onAnswerChange={onAnswerChange} />
            <UnlabeledMultipleChoiceQuestion checkedItem={getAnswered(65)} refProp={questionRefs[30]} question="I have to think of special thoughts (like numbers or words) to stop bad things from happening" answers={answers} questionId={65} onAnswerChange={onAnswerChange} />
            <UnlabeledMultipleChoiceQuestion checkedItem={getAnswered(66)} refProp={questionRefs[31]} question="I worry what other people think of me" answers={answers} questionId={66} onAnswerChange={onAnswerChange} />
            <UnlabeledMultipleChoiceQuestion checkedItem={getAnswered(67)} refProp={questionRefs[32]} question="I am afraid of being in crowded places (like shopping centers, the movies, buses, busy playgrounds)" answers={answers} questionId={67} onAnswerChange={onAnswerChange} />
            <UnlabeledMultipleChoiceQuestion checkedItem={getAnswered(68)} refProp={questionRefs[33]} question="All of a sudden I feel really scared for no reason at all" answers={answers} questionId={68} onAnswerChange={onAnswerChange} />
            <UnlabeledMultipleChoiceQuestion checkedItem={getAnswered(69)} refProp={questionRefs[34]} question="I worry about what is going to happen" answers={answers} questionId={69} onAnswerChange={onAnswerChange} />
            <UnlabeledMultipleChoiceQuestion checkedItem={getAnswered(70)} refProp={questionRefs[35]} question="I suddenly become dizzy or faint when there is no reason for this" answers={answers} questionId={70} onAnswerChange={onAnswerChange} />
            <UnlabeledMultipleChoiceQuestion checkedItem={getAnswered(71)} refProp={questionRefs[36]} question="I think about death" answers={answers} questionId={71} onAnswerChange={onAnswerChange} />
            <UnlabeledMultipleChoiceQuestion checkedItem={getAnswered(72)} refProp={questionRefs[37]} question="I feel afraid if I have to talk in front of my class" answers={answers} questionId={72} onAnswerChange={onAnswerChange} />
            <UnlabeledMultipleChoiceQuestion checkedItem={getAnswered(73)} refProp={questionRefs[38]} question="My heart suddenly starts to beat too quickly for no reason" answers={answers} questionId={73} onAnswerChange={onAnswerChange} />
            <UnlabeledMultipleChoiceQuestion checkedItem={getAnswered(74)} refProp={questionRefs[39]} question="I feel like I don’t want to move" answers={answers} questionId={74} onAnswerChange={onAnswerChange} />
            <UnlabeledMultipleChoiceQuestion checkedItem={getAnswered(75)} refProp={questionRefs[40]} question="I worry that I will suddenly get a scared feeling when there is nothing to be afraid of" answers={answers} questionId={75} onAnswerChange={onAnswerChange} />
            <UnlabeledMultipleChoiceQuestion checkedItem={getAnswered(76)} refProp={questionRefs[41]} question="I have to do some things over and over again (like washing my hands, cleaning or putting things in a certain order)" answers={answers} questionId={76} onAnswerChange={onAnswerChange} />
            <UnlabeledMultipleChoiceQuestion checkedItem={getAnswered(77)} refProp={questionRefs[42]} question="I feel afraid that I will make a fool of myself in front of people" answers={answers} questionId={77} onAnswerChange={onAnswerChange} />
            <UnlabeledMultipleChoiceQuestion checkedItem={getAnswered(78)} refProp={questionRefs[43]} question="I have to do some things in just the right way to stop bad things from happening" answers={answers} questionId={78} onAnswerChange={onAnswerChange} />
            <UnlabeledMultipleChoiceQuestion checkedItem={getAnswered(79)} refProp={questionRefs[44]} question="I worry when I go to bed at night" answers={answers} questionId={79} onAnswerChange={onAnswerChange} />
            <UnlabeledMultipleChoiceQuestion checkedItem={getAnswered(80)} refProp={questionRefs[45]} question="I would feel scared if I had to stay away from home overnight" answers={answers} questionId={80} onAnswerChange={onAnswerChange} />
            <UnlabeledMultipleChoiceQuestion checkedItem={getAnswered(81)} refProp={questionRefs[46]} question="I feel restless" answers={answers} questionId={81} onAnswerChange={onAnswerChange} />
        </QuestionList>
        <ButtonContainer>
            {loading && <LoadingIndicator />}
            {!loading && <Button text="Save answers" onClick={goToNextPage} onDisabledClick={onDisabledClick} />}
        </ButtonContainer>
        <ProgressBar length={47} progress={numberAnswered} />
        <ErrorToast role="alert" aria-atomic="true" className={error ? "sliding" : "hidden"}>{error}</ErrorToast>
        <ConfirmationToast className={saveDialog ? "sliding" : "hidden"}>
            <DialogContentText className="DialogContentText">Not all questions have been answered. Do you want to save anyway?</DialogContentText>
            <DialogActions className="DialogActions">
                <Button margin="0 4px" refProp={htmlElRef} onClick={closeDialog} text={"Cancel"} />
                <Button margin="0 4px" onClick={saveForm} text={"Save"} />
            </DialogActions>
        </ConfirmationToast>
    </ContentWrapper>;
};

export default RcadsPage1;


// WEBPACK FOOTER //
// ./src/Forms/rcads/RcadsPage1.tsx